import { useCallback, useMemo, useState } from 'react';

import useEvent from '@lyearn/core/hooks/useEvent';
import { useTranslation } from '@lyearn/i18n';
import { getInputPlaceholderPlugin, getPMDocFromHtml } from '@lyearn/lygo-blocks';
import type { BaseExtension, EditorView } from '@lyearn/lygo-core';
import { getSchema, Schema } from '@lyearn/lygo-core';
import { EntityMediaFragment } from '@/features/messages/graphql/fragments/generated/MediaType';
import { PostFragment } from '@/features/messages/graphql/fragments/generated/Post';
import useAttachmentUpload, { adaptMedias } from '@/features/messages/hooks/useAttachmentUpload';
import { TASK_INSTRUCTION_EDITOR_EXTENSIONS as extensions } from '@/lygo/TaskInstructionEditor';

import { TaskDetailsInstructionProps } from '../../types';

const getMediaUrl = (media: EntityMediaFragment) => media.url;

const useTaskInstructionEditor = ({ value, onChange }: TaskDetailsInstructionProps) => {
  const { t } = useTranslation();
  const [text, setText] = useState(value?.text || '');
  const [editorView, setEditorView] = useState<EditorView | undefined>();

  /**
   * isEditorFocused state
   * on clicking link tags when not in edit mode, it should redirect to link
   * However in current Editor, this does not happen since editor focus event is triggered first then onClick is triggered
   * to handle this, isEditorFocused is used which we set after onClick is triggered
   */
  const [isEditorFocused, setEditorFocused] = useState(false);
  const editorProps = useMemo(() => {
    const { nodes, marks, plugins, nodePerformanceMap, inputRules } = getSchema(extensions as any);
    const schema = new Schema({ marks, nodes });

    return {
      schema,
      doc: getPMDocFromHtml(text, schema) || undefined,
      nodePerformanceMap: nodePerformanceMap as any,
      inputRules,
      plugins: [...plugins, ...getInputPlaceholderPlugin(t('Task:AddDescription'))],
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const onEditorChange = useEvent(() => {
    if (!editorView) {
      return;
    }
    const content = editorView.dom.textContent ? editorView.dom.innerHTML : '';
    setText(`<div class="editor-content-wrapper max-w-full">${content}</div>`);
  });

  const onCreateEditorView = useEvent((createdEditorView: EditorView) => {
    setEditorView(createdEditorView);
  });

  const onEditorBlur = useEvent(() => {
    onChange({ text, medias: adaptMedias(medias) });
    setEditorFocused(false);
  });

  const onEditorClick = useEvent((event) => {
    if (!isEditorFocused) {
      if (event.target.tagName.toLowerCase() === 'a') {
        window.open(event.target.href);
        return;
      }
    }

    setEditorFocused(true);
  });

  const attachments = useMemo<PostFragment['attachments']>(() => {
    if (!value?.medias?.length) {
      return [];
    }
    return value.medias.map((media) => ({ id: media.mediaId, media }));
  }, [value?.medias]);

  const { uppy, files, removeFile } = useAttachmentUpload({ attachments });

  const onRemoveMedia = useEvent((media: EntityMediaFragment) => {
    const localFile = files[media.mediaId];
    if (localFile) {
      removeFile(localFile);
    }
  });

  const { medias, mediasKey } = useMemo(() => {
    const filesValues = Object.values(files);
    return {
      medias: filesValues,
      mediasKey: filesValues.filter(getMediaUrl).map(getMediaUrl).join(''),
    };
  }, [files]);
  // removing useUpdateEffect to avoid resetting text when image is inserted
  // useUpdateEffect(() => {
  //   onChange({ text, medias: adaptMedias(medias.filter(getMediaUrl)) });
  // }, [mediasKey]);

  const removeLoadingStates = useCallback(() => {
    if (editorView) {
      const { tr } = editorView.state;
      let hasEmptyImages = false;

      editorView.state.doc.descendants((node, pos) => {
        if (node.type.name === 'image' && (!node.attrs.src || node.attrs.src === '')) {
          tr.delete(pos, pos + node.nodeSize);
          hasEmptyImages = true;
        }
      });

      if (hasEmptyImages) {
        editorView.dispatch(tr);
      }
    }
  }, [editorView]);

  const onImageInserted = useCallback(
    (newText: string) => {
      setText(newText);
      onChange({ text: newText, medias: adaptMedias(medias.filter(getMediaUrl)) });
      removeLoadingStates();
    },
    [medias, onChange, removeLoadingStates],
  );

  return {
    uppy,
    onEditorBlur,
    onEditorClick,
    editorProps: {
      ...editorProps,
      onCreateEditorView,
      onChange: onEditorChange,
      plugins: editorProps.plugins as BaseExtension['plugins'][],
    },
    mediaContainerProps: {
      medias,
      onRemoveMedia,
    },
    editorView,
    onImageInserted,
    removeLoadingStates,
  };
};

export type UseTaskInstructionEditorReturnType = ReturnType<typeof useTaskInstructionEditor>;
export default useTaskInstructionEditor;
